// components/dashboard/AnalyticsDashboard.js
import React, { useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { 
  TrendingUp, Clock, MessageSquare, Star, 
  Users, Calendar, Download, RefreshCw, Mail, Send
} from 'lucide-react';

import UsageAnalyticsWidget from './UsageAnalyticsWidget';

const AnalyticsDashboard = ({ api }) => {
  const [analytics, setAnalytics] = useState({
    overview: {},
    reviewStats: {},
    ticketStats: {},
    teamPerformance: [],
    trends: [],
    ratingDistribution: [],
    loading: true
  });
  const [dateRange, setDateRange] = useState('30d');
  const [refreshing, setRefreshing] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const COLORS = {
    5: '#10B981', // Green
    4: '#3B82F6', // Blue
    3: '#F59E0B', // Amber
    2: '#EF4444', // Red
    1: '#DC2626'  // Dark Red
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setRefreshing(true);
    try {
      const [overview, reviewStats, ticketStats, teamPerf, trends, ratingDist] = await Promise.all([
        api.request('/analytics/overview?range=' + dateRange),
        api.request('/analytics/reviews?range=' + dateRange),
        api.request('/analytics/tickets?range=' + dateRange),
        api.request('/analytics/team-performance?range=' + dateRange),
        api.request('/analytics/trends?range=' + dateRange),
        api.request('/analytics/rating-distribution?range=' + dateRange)
      ]);

      setAnalytics({
        overview: overview.data || {},
        reviewStats: reviewStats.data || {},
        ticketStats: ticketStats.data || {},
        teamPerformance: teamPerf.data || [],
        trends: trends.data || [],
        ratingDistribution: ratingDist.data || [],
        loading: false
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setAnalytics({
        overview: {},
        reviewStats: {},
        ticketStats: {},
        teamPerformance: [],
        trends: [],
        ratingDistribution: [],
        loading: false
      });
    }
    setRefreshing(false);
  };

  const sendWeeklyReport = async () => {
    setSendingReport(true);
    try {
      await api.request('/analytics/send-weekly-report', { method: 'POST' });
      alert('Weekly report sent successfully!');
    } catch (error) {
      console.error('Failed to send report:', error);
      alert('Failed to send report: ' + error.message);
    }
    setSendingReport(false);
  };

  const exportData = async () => {
    try {
      // Request PDF from backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/analytics/export-pdf?range=${dateRange}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get the PDF blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to generate PDF report');
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (analytics.loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const { overview, reviewStats, ticketStats, teamPerformance, trends, ratingDistribution } = analytics;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-sm text-gray-500">Monitor reviews, tickets, and team performance</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
 
          <button
            onClick={fetchAnalytics}
            disabled={refreshing}
            className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={exportData}
            className="p-2 border rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Overview Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Reviews</p>
              <p className="text-2xl font-bold">{overview.totalReviews || 0}</p>
              <p className="text-xs text-gray-400 mt-1">
                Avg Rating: {overview.averageRating?.toFixed(1) || '0.0'} ⭐
              </p>
            </div>
            <Star className="w-8 h-8 text-yellow-500 opacity-50" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Reviews</p>
              <p className="text-2xl font-bold">{overview.pendingReviews || 0}</p>
              <p className="text-xs text-red-500 mt-1">
                {overview.urgentReviews || 0} urgent (≤2★)
              </p>
            </div>
            <MessageSquare className="w-8 h-8 text-red-500 opacity-50" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Tickets</p>
              <p className="text-2xl font-bold">{overview.totalTickets || 0}</p>
              <p className="text-xs text-gray-400 mt-1">
                {overview.openTickets || 0} open
              </p>
            </div>
            <Mail className="w-8 h-8 text-blue-500 opacity-50" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Response Time</p>
              <p className="text-2xl font-bold">
                {overview.avgResponseTime ? `${overview.avgResponseTime.toFixed(1)}h` : 'N/A'}
              </p>
              <p className="text-xs text-green-500 mt-1">
                {overview.responseRate || 0}% response rate
              </p>
            </div>
            <Clock className="w-8 h-8 text-green-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Review & Ticket Stats Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <h3 className="text-lg font-semibold mb-3">Review Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Response Rate</span>
              <span className="text-lg font-bold text-blue-600">
                {reviewStats.responseRate || 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Response Time</span>
              <span className="text-lg font-bold text-green-600">
                {reviewStats.avgResponseTime ? `${reviewStats.avgResponseTime.toFixed(1)}h` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Reviews This Period</span>
              <span className="text-lg font-bold text-purple-600">
                {reviewStats.totalReviews || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <h3 className="text-lg font-semibold mb-3">Ticket Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Resolution Rate</span>
              <span className="text-lg font-bold text-blue-600">
                {ticketStats.resolutionRate || 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Resolution Time</span>
              <span className="text-lg font-bold text-green-600">
                {ticketStats.avgResolutionTime ? `${ticketStats.avgResolutionTime.toFixed(1)}h` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tickets This Period</span>
              <span className="text-lg font-bold text-purple-600">
                {ticketStats.totalTickets || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rating Distribution - Pie Chart */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Rating Distribution</h3>
          {ratingDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={ratingDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ rating, count, percent }) => `${rating}★: ${count} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {ratingDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.rating] || '#6B7280'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              No rating data available
            </div>
          )}
        </div>

        {/* Trends Over Time - Line Chart */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Activity Trends</h3>
          {trends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="reviews" 
                  stroke="#F59E0B" 
                  strokeWidth={2}
                  name="Reviews"
                />
                <Line 
                  type="monotone" 
                  dataKey="tickets" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  name="Tickets"
                />
                <Line 
                  type="monotone" 
                  dataKey="responses" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  name="Responses"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              No trend data available
            </div>
          )}
        </div>

        {/* Team Performance Leaderboard */}
        <div className="bg-white p-6 rounded-lg border lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Team Performance Leaderboard</h3>
          {teamPerformance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">Rank</th>
                    <th className="text-left py-2 px-4">Team Member</th>
                    <th className="text-right py-2 px-4">Reviews Handled</th>
                    <th className="text-right py-2 px-4">Tickets Resolved</th>
                    <th className="text-right py-2 px-4">Avg Response Time</th>
                    <th className="text-right py-2 px-4">Total Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamPerformance.map((member, index) => (
                    <tr key={member.userId} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                          index === 0 ? 'bg-yellow-100 text-yellow-800' :
                          index === 1 ? 'bg-gray-100 text-gray-800' :
                          index === 2 ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-50 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium">{member.name}</td>
                      <td className="py-3 px-4 text-right">{member.reviewsHandled || 0}</td>
                      <td className="py-3 px-4 text-right">{member.ticketsResolved || 0}</td>
                      <td className="py-3 px-4 text-right">
                        {member.avgResponseTime ? `${member.avgResponseTime.toFixed(1)}h` : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-right font-bold">
                        {(member.reviewsHandled || 0) + (member.ticketsResolved || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400">
              No team performance data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;